import type {Assignment} from "@khan/assignments-types";

import {ContentCodeFromKind} from "#content-library/ui/utils/content-util.ts";

/**
 * Builds a URL for content within the assignment's course context by
 * reconstructing it with DCUL information.
 *
 * This ensures teachers navigate to the content in the course context where
 * it was assigned, preventing navigation to different courses.
 *
 * For example, if a teacher assigns an exercise from Algebra 1, this will
 * ensure the link goes to /math/algebra/unit/lesson/e/exercise-slug
 * instead of potentially /math/algebra-2/unit/lesson/e/exercise-slug
 */
export const buildURLForAssignment = (
    assignment: Assignment,
): string | null => {
    const content = assignment.contents?.[0];

    if (!content) {
        return null;
    }

    if (
        content.kind === "CourseChallenge" ||
        content.kind === "AIGuideActivity"
    ) {
        return content.defaultUrlPath;
    }

    // Get the content code for the URL (e.g., "e" for Exercise, "v" for Video)
    const contentCode =
        ContentCodeFromKind[content.kind as keyof typeof ContentCodeFromKind];
    if (!contentCode) {
        return content.defaultUrlPath;
    }

    const matchingPath = content.topicPaths?.find((topicPath) =>
        topicPath.path?.some((segment) => segment.id === assignment.courseId),
    );
    if (!matchingPath) {
        return content.defaultUrlPath;
    }

    return `/${matchingPath.path?.[0].slug}/${matchingPath.path?.[1].slug}/${matchingPath.path?.[2].slug}/${matchingPath.path?.[3].slug}/${contentCode}/${matchingPath.path?.[4].slug}`;
};
